import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Divider,
  Modal,
  Checkbox,
  createTheme,
  ThemeProvider,
  InputLabel,
  FormControl,
  Accordion,
  AccordionSummary,
  Table,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  TableBody,
  Paper,
  AccordionDetails,
  Grid2,
  IconButton,
} from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "@fontsource/manrope";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { ReactComponent as GenerateButton } from "../../images/generateButton.svg";
import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import { getJIRABugsByID } from "../../services/webServices";

const theme = createTheme({
  typography: {
    fontFamily: "Manrope, Arial, sans-serif",
  },
});

const BugsFiltersPage = ({
  filters,
  setOpenFiltersPage,
  setParent,
  setFilterSubmit,
  projectKey,
  setSprintsList,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [accordionExpanded, setAccordionExpanded] = useState(true);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [bugID, setBugID] = useState("");
  const [defects, setDefects] = useState([]);
  const [bugIDError, setBugIDError] = useState("");

  const parseADF = (content) => {
    const parseNode = (node) => {
      if (!node) return "";

      switch (node.type) {
        case "paragraph":
          return node.content?.map(parseNode).join("") + "\n";

        case "heading":
          const headingText = node.content?.map(parseNode).join("");
          const hashes = "#".repeat(node.attrs?.level || 1);
          return `${hashes} ${headingText}\n\n`;

        case "text":
          let text = node.text || "";
          if (node.marks) {
            node.marks.forEach((mark) => {
              if (mark.type === "strong") text = `**${text}**`;
              if (mark.type === "em") text = `_${text}_`;
              if (mark.type === "code") text = `\`${text}\``;
            });
          }
          return text;

        case "bulletList":
          return node.content?.map(parseNode).join("") + "\n";

        case "orderedList":
          return (
            node.content
              ?.map((item, index) => parseNode(item, index))
              .join("") + "\n"
          );

        case "listItem":
          return node.content
            ?.map((child) => `- ${parseNode(child)}`)
            .join("\n");

        case "blockquote":
          return node.content
            ?.map((child) => `> ${parseNode(child)}`)
            .join("\n");

        case "codeBlock":
          return "```\n" + node.content?.map(parseNode).join("") + "\n```\n";

        default:
          return node.content?.map(parseNode).join("") || "";
      }
    };

    return content.map(parseNode).join("").trim();
  };

  const handleBugIDs = async () => {
    if (!bugID.trim()) return;

    setBugIDError(""); // Clear previous error

    const bugIDs = bugID
      .split(/[\s,]+/)
      .map((id) => id.trim())
      .filter((id) => id && !defects.some((defect) => defect.key === id));

    if (bugIDs.length === 0) return;

    try {
      const bugIDResponse = await getJIRABugsByID({
        bugID: bugIDs,
        projectKey: projectKey,
      });

      if (!bugIDResponse.data || bugIDResponse.data.length === 0) {
        setBugIDError("Invalid Bug ID detected");
        return;
      }

      const newDefects = bugIDResponse.data.map((bugData) => {
        let combinedDescription = "";

        const adfDescription = bugData.fields.description;
        if (adfDescription && Array.isArray(adfDescription.content)) {
          combinedDescription = parseADF(adfDescription.content);
        }

        return {
          id: bugData.id,
          key: bugData.key,
          title: bugData.fields.summary,
          description: combinedDescription,
          priority: bugData.fields.priority.name,
          status: bugData.fields.status.name,
        };
      });

      if (newDefects.length === 0) {
        setBugIDError("No valid Bug IDs found.");
        return;
      }

      setDefects((prevDefects) => [...prevDefects, ...newDefects]);
      setAccordionExpanded(false);
      setBugID(""); // Clear field
      setBugIDError(""); // Clear error if success
    } catch (error) {
      setBugIDError("Error fetching Bug IDs. Please try again.");
      console.error("Bug ID fetch error:", error);
    }
  };

  const initialFilters = Object.keys(filters).reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});

  const [selectedFilters, setSelectedFilters] = useState(initialFilters);

  const handleResetFilters = () => {
    setSelectedFilters(initialFilters);
    setIsFilterApplied(false);
  };

  const handleFilterChange = (filterName) => (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      handleSelectAll(filterName);
    } else {
      setSelectedFilters((prevState) => {
        const updatedFilters = {
          ...prevState,
          [filterName]: value,
        };
        updateIsFilterApplied(updatedFilters);
        return updatedFilters;
      });
    }
  };

  // Handles "Select All" logic
  const handleSelectAll = (filterName) => {
    const allValues = filters[filterName].map((eachFilter) => eachFilter.value);
    const isAllSelected =
      selectedFilters[filterName]?.length === allValues.length;

    setSelectedFilters((prevState) => {
      const updatedFilters = {
        ...prevState,
        [filterName]: isAllSelected ? [] : allValues, // Toggle selection
      };

      updateIsFilterApplied(updatedFilters);
      return updatedFilters;
    });
  };

  // Handles individual item selection
  const handleItemClick = (filterName, value) => {
    setSelectedFilters((prevState) => {
      const prevValues = prevState[filterName] || [];
      const newValues = prevValues.includes(value)
        ? prevValues.filter((v) => v !== value) // Remove if already selected
        : [...prevValues, value]; // Add if not selected

      const updatedFilters = {
        ...prevState,
        [filterName]: newValues,
      };

      updateIsFilterApplied(updatedFilters);
      return updatedFilters;
    });
  };

  // Clears selections for a specific filter
  const handleClearSelection = (filterName) => {
    setSelectedFilters((prevState) => {
      const updatedFilters = {
        ...prevState,
        [filterName]: [], // Clear selected dropdown
      };

      updateIsFilterApplied(updatedFilters);
      return updatedFilters;
    });
  };

  // Centralized function to update `isFilterApplied`
  const updateIsFilterApplied = (filters) => {
    const isAnyFilterApplied = Object.values(filters).some(
      (selected) => selected.length > 0
    );
    setIsFilterApplied(isAnyFilterApplied);
  };

  const handleClose = () => {
    setOpenFiltersPage(false);
  };

  const handleImportBugs = async () => {
    // console.log("defects", defects);
    navigate("/dashboard/multi-bugreports/input", {
      state: { bugDetails: defects },
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Modal
        open={isModalOpen}
        aria-labelledby="import-bug-description-title"
        aria-describedby="import-bug-description-instructions"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "600px",
            height: "550px", // Fixed modal height
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 3,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            outline: "none",
          }}
        >
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: "5px" }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                sx={{ color: "black", "& svg": { fontSize: "0.9rem" } }}
                onClick={() => {
                  setParent(null);
                  setSprintsList([]);
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "normal",
                  color: "black",
                  fontSize: "1.0rem",
                }}
              >
                Import Bug Description
              </Typography>
            </Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Instructions */}
          <Typography
            id="import-bug-description-instructions"
            variant="subtitle2"
            sx={{ textAlign: "left", color: "text.secondary", mb: 4 }}
          >
            Enter details or select parameters to generate results
          </Typography>

          <Grid2 container spacing={2} alignItems="center">
            <Grid2>
              <TextField
                label="Defect ID(s)"
                placeholder="e.g., BUG-101, BUG-202"
                variant="outlined"
                size="small"
                value={bugID}
                onChange={(e) => {
                  setBugID(e.target.value);
                  if (bugIDError) setBugIDError(""); // Clear error on change
                }}
                error={!!bugIDError}
                helperText={bugIDError}
                sx={{ width: "220px" }}
              />
            </Grid2>

            <Grid2>
              <Button
                variant="contained"
                onClick={handleBugIDs}
                sx={{
                  textTransform: "none",
                  height: 36,
                  borderRadius: "8px",
                  px: "16px",
                  py: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  whiteSpace: "nowrap",
                  backgroundColor: "#2b78ebff", // Dark Blue
                  color: "#FFFFFF", // White text
                  fontFamily: "Manrope",
                  fontSize: "14px",
                  "&:hover": {
                    backgroundColor: "#08306B", // Even darker on hover
                  },
                }}
              >
                Fetch Bug Details
              </Button>
            </Grid2>
          </Grid2>

          {defects.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                mt: 4,
                mb: 4,
                borderRadius: 2,
                border: "1px solid #E0E0E0", // Light border around the entire table
                boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.1)", // Subtle shadow for elevation
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ borderBottom: "1px solid #E0E0E0" }}>
                    <TableCell
                      sx={{
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        padding: "4px 12px",
                        borderRight: "1px solid #ddd",
                      }}
                    >
                      Bug ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        padding: "4px 12px",
                        borderRight: "1px solid #ddd",
                      }}
                    >
                      Summary
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        padding: "4px 12px",
                        borderRight: "1px solid #ddd",
                      }}
                    >
                      Priority
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        padding: "4px 12px",
                        borderRight: "1px solid #ddd",
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        fontFamily: "Manrope",
                        fontSize: "12px",
                        padding: "4px 12px",
                        borderRight: "1px solid #ddd",
                      }}
                    ></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {defects.map((defect, index) => (
                    <TableRow
                      key={index}
                      sx={{ borderBottom: "1px solid #E0E0E0" }}
                    >
                      {" "}
                      {/* Border between rows */}
                      <TableCell
                        sx={{
                          borderRight: "1px solid #E0E0E0",
                          padding: "8px",
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          color: "#000000",
                        }}
                      >
                        {defect.key}
                      </TableCell>
                      <TableCell
                        sx={{
                          borderRight: "1px solid #E0E0E0",
                          padding: "8px",
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          color: "#000000",
                        }}
                      >
                        {defect.title}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          borderRight: "1px solid #E0E0E0",
                          padding: "8px",
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          color: "#CA0000",
                        }}
                      >
                        {defect.priority}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          borderRight: "1px solid #E0E0E0",
                          padding: "8px",
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          color: "#CA0000",
                        }}
                      >
                        {defect.status}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: "center",
                          padding: "8px",
                          fontFamily: "Manrope",
                          fontSize: "14px",
                          color: "#000000",
                        }}
                      >
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() =>
                            setDefects(
                              defects.filter((d) => d.id !== defect.id)
                            )
                          }
                        >
                          <RemoveCircleOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Accordion for Filters */}
            {defects.length === 0 && (
              <Accordion
                expanded={accordionExpanded}
                onChange={() => setAccordionExpanded(!accordionExpanded)}
                sx={{
                  flexGrow: 1,
                  boxShadow: "none",
                  border: "none",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="filter-content"
                  id="filter-header"
                  sx={{
                    flexDirection: "row-reverse",
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      marginRight: "auto",
                    },
                    mb: -1,
                    ml: -2,
                  }}
                >
                  <Typography>More Filters</Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    height: "200px", // Reserve space for accordion content
                    overflow: "hidden",
                    display: accordionExpanded ? "block" : "none", // Show or hide content without resizing
                    ml: -2,
                  }}
                >
                  <Grid2 container spacing={3}>
                    {Object.entries(filters).map(
                      ([filterName, filterValue]) => (
                        <Grid2 item xs={4} key={filterName}>
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            sx={{ width: 250, marginBottom: 3 }}
                          >
                            <InputLabel id={`${filterName}-filter-label`}>
                              {filterName}
                            </InputLabel>
                            <Select
                              labelId={`${filterName}-filter-label`}
                              multiple
                              value={selectedFilters[filterName] || []}
                              onChange={handleFilterChange(filterName)}
                              label={filterName} // Important to prevent label merging issue
                              renderValue={(selected) => {
                                if (selected.length === 0) return "Select"; // Show default text if nothing is selected

                                const selectedNames = selected
                                  .map(
                                    (selectedValue) =>
                                      filters[filterName].find(
                                        (eachFilter) =>
                                          eachFilter.value === selectedValue
                                      )?.name
                                  )
                                  .filter(Boolean); // Remove undefined values

                                return selectedNames.length === 1
                                  ? selectedNames[0]
                                  : `${selectedNames[0]} (+${
                                      selectedNames.length - 1
                                    })`;
                              }}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                              }}
                              MenuProps={{
                                PaperProps: {
                                  style: { maxHeight: 200, overflow: "auto" },
                                },
                              }}
                              endAdornment={
                                selectedFilters[filterName]?.length > 0 && (
                                  <IconButton
                                    onClick={() =>
                                      handleClearSelection(filterName)
                                    }
                                    size="small"
                                    sx={{ marginRight: 2 }}
                                  >
                                    <ClearIcon fontSize="small" />
                                  </IconButton>
                                )
                              }
                            >
                              {/* Select All Checkbox for Sprint */}
                              {filterName === "Sprint" &&
                                filterValue.length > 0 && (
                                  <MenuItem
                                    key="select-all"
                                    value="all"
                                    onClick={() => handleSelectAll(filterName)}
                                  >
                                    <Checkbox
                                      checked={
                                        selectedFilters[filterName]?.length ===
                                        filterValue.length
                                      }
                                    />
                                    Select All
                                  </MenuItem>
                                )}

                              {/* Individual checkboxes */}
                              {filterValue.length === 0 ? (
                                <MenuItem disabled>
                                  No {filterName} Available
                                </MenuItem>
                              ) : (
                                filterValue.map((eachFilter) => (
                                  <MenuItem
                                    key={eachFilter.value}
                                    value={eachFilter.value}
                                    onClick={() =>
                                      handleItemClick(
                                        filterName,
                                        eachFilter.value
                                      )
                                    }
                                  >
                                    <Checkbox
                                      checked={selectedFilters[
                                        filterName
                                      ]?.includes(eachFilter.value)}
                                    />
                                    {eachFilter.name}
                                  </MenuItem>
                                ))
                              )}
                            </Select>
                          </FormControl>
                        </Grid2>
                      )
                    )}
                  </Grid2>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>

          {/* Divider */}
          <Divider sx={{ marginY: 2 }} />

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <a
              style={{
                display: "flex",
                alignItems: "center",
                cursor: isFilterApplied ? "pointer" : "default",
                textDecoration: "none",
                color: isFilterApplied ? "#1976d2" : "#9e9e9e", // Changed to blue when enabled
                fontWeight: 400,
                pointerEvents: isFilterApplied ? "auto" : "none",
              }}
              onClick={isFilterApplied ? handleResetFilters : undefined}
            >
              <RestartAltIcon
                style={{
                  marginRight: 4,
                  color: isFilterApplied ? "#1976d2" : "#9e9e9e", // Changed to blue when enabled
                }}
              />
              <Typography
                variant="body2"
                style={{
                  color: isFilterApplied ? "#1976d2" : "#9e9e9e", // Changed to blue when enabled
                  fontWeight: 400,
                }}
              >
                Reset Filter
              </Typography>
            </a>
            {defects.length === 0 && (
              <Button
                variant="contained"
                size="small"
                // startIcon={<GenerateButton />}
                sx={{
                  backgroundColor: isFilterApplied ? "#1976d2" : "#f0f0f0",
                  color: isFilterApplied ? "#ffffff" : "#9e9e9e",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: isFilterApplied ? "#1565c0" : "#f0f0f0",
                  },
                }}
                disabled={!isFilterApplied}
                onClick={() => setFilterSubmit(selectedFilters)}
              >
                Search
              </Button>
            )}
            {defects.length > 0 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<GenerateButton />}
                onClick={handleImportBugs}
              >
                Import
              </Button>
            )}
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default BugsFiltersPage;
